import axios from "axios";
import { store } from "../redux/store";
import { logout, setCredentials } from "../redux/slices/authSlice";

const BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ─────────────────────────────────────────────
// REQUEST INTERCEPTOR
// ─────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = store.getState().auth.accessToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ─────────────────────────────────────────────
// REFRESH CONTROL
// ─────────────────────────────────────────────
// Single shared in-flight refresh promise — used by BOTH the 401 interceptor
// below AND authSlice's initAuth (app-bootstrap refresh). Before this was
// shared, initAuth called /auth/refresh directly, completely bypassing this
// module's dedup — if it ever fired around the same time as an interceptor-
// triggered refresh (or fired twice for any reason: a second tab open,
// a fast reload), TWO refresh requests could go out carrying the same
// still-valid refresh-token cookie before either response landed. The
// server treats the second one to arrive as token reuse and hard-fails it,
// which used to log the user straight out. Now every caller in this tab
// awaits the exact same network call instead of each starting their own.
let refreshPromise = null;

export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = axios
    .post(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true })
    .then((res) => {
      const accessToken = res.data.data.accessToken;
      store.dispatch(setCredentials({ accessToken }));
      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

let queue = [];

const processQueue = (error, token = null) => {
  queue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });

  queue = [];
};

// ─────────────────────────────────────────────
// RESPONSE INTERCEPTOR
// ─────────────────────────────────────────────
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If a refresh is already running (from this interceptor OR from
    // initAuth) → queue this request behind it instead of starting another.
    if (refreshPromise) {
      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    try {
      const accessToken = await refreshAccessToken();
      processQueue(null, accessToken);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (err) {
      processQueue(err, null);
      store.dispatch(logout());
      return Promise.reject(err);
    }
  },
);

export default api;
